/**
 * CalendarHeader — title/range, prev/next navigation (bounded to the data
 * range), the Day / 5-day / Week view switcher, and the Compact/Full density
 * toggle. View-only: every control mutates the renderer's local UI state.
 */

import { ChevronLeft, ChevronRight, List, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/cn';
import { CalendarSettingsMenu } from './CalendarSettingsMenu';
import type { CalendarView, CalendarDensity, TimezoneSpec } from './calendar.types';

const VIEW_OPTIONS: { value: CalendarView; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'fiveDay', label: '5-day' },
  { value: 'week', label: 'Week' },
];

interface SegmentProps<T extends string> {
  options: { value: T; label: string; icon?: React.ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}

function Segmented<T extends string>({ options, value, onChange, ariaLabel }: SegmentProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted/40 p-0.5"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus-ring',
              active
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export interface CalendarHeaderProps {
  view: CalendarView;
  density: CalendarDensity;
  rangeLabel: string;
  /** Secondary line under the range (e.g. "12 events"). */
  subLabel?: string;
  onViewChange: (v: CalendarView) => void;
  onDensityChange: (d: CalendarDensity) => void;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
  /** Provided only when today is within the data range; jumps to today. */
  onToday?: () => void;
  /** Settings menu state (persisted component settings). */
  hour12: boolean;
  timezones: TimezoneSpec[];
  onHour12Change: (v: boolean) => void;
  onTimezonesChange: (tzs: TimezoneSpec[]) => void;
}

export function CalendarHeader({
  view,
  density,
  rangeLabel,
  subLabel,
  onViewChange,
  onDensityChange,
  onPrev,
  onNext,
  canPrev,
  canNext,
  onToday,
  hour12,
  timezones,
  onHour12Change,
  onTimezonesChange,
}: CalendarHeaderProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
      {/* Left: nav + range */}
      <div className="flex items-center gap-2">
        <div className="flex items-center">
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            aria-label="Previous"
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            aria-label="Next"
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-foreground">{rangeLabel}</h3>
          {subLabel && <p className="truncate text-xs text-muted-foreground">{subLabel}</p>}
        </div>
      </div>

      {/* Right: today + density + view switchers */}
      <div className="flex items-center gap-2">
        {onToday && (
          <button
            type="button"
            onClick={onToday}
            className="shrink-0 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-ring"
          >
            Today
          </button>
        )}
        <Segmented<CalendarDensity>
          ariaLabel="Density"
          value={density}
          onChange={onDensityChange}
          options={[
            { value: 'compact', label: 'List', icon: <List className="size-3.5" /> },
            { value: 'full', label: 'Timeline', icon: <LayoutGrid className="size-3.5" /> },
          ]}
        />
        <Segmented<CalendarView>
          ariaLabel="View"
          value={view}
          onChange={onViewChange}
          options={VIEW_OPTIONS}
        />
        <CalendarSettingsMenu
          hour12={hour12}
          timezones={timezones}
          onHour12Change={onHour12Change}
          onTimezonesChange={onTimezonesChange}
        />
      </div>
    </div>
  );
}
